import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getCurrentUserId } from '../../user/currentUserSelector';
import { createSelectIsElementSingleSelected } from '../selection/selectedElementsSelector';
import { createSelectLabelsForElement } from '../labels/labelSelectors';
import { getTaskItemChipDescriptors } from './taskItemChipsUtils';
import type { TaskItemChipSource, UpdateTaskItemAttrs } from './taskItemChipsUtils';
import { ChipSelectionScopeProvider } from './behaviours/ChipSelectionScope';
import TaskItemChip from './taskItemChips/TaskItemChip';
import { ChipBaseProps } from '../../../common/@roma/src/components/base/chip/ChipBase';

type TaskItemChipsContainerProps = {
    /** The owning task node's chip-relevant attrs (plus `checked`). */
    attrs: TaskItemChipSource;
    /** Updates the owning task node's attrs (the node view's `updateAttributes`). */
    updateAttributes: UpdateTaskItemAttrs;
    /** Whether chips can be modified. Defaults to true for the Storybook POC (no permission context). */
    canModify?: boolean;
    /** The owning canvas element whose single-selection gates chip activation. */
    elementId: string;
    size: ChipBaseProps['size'];
};

/**
 * Renders the data-based chips for a single Tiptap task item; each chip
 * implementation mutates the owning node's attrs (rather than dispatching
 * element-keyed redux actions) for every interaction.
 */
const TaskItemChipsContainer: React.FC<TaskItemChipsContainerProps> = ({
    attrs,
    updateAttributes,
    canModify = true,
    elementId,
    size = 'xs',
}) => {
    const currentUserId = useSelector(getCurrentUserId);

    const selectIsElementSingleSelected = useMemo(() => createSelectIsElementSingleSelected(elementId), [elementId]);
    const isElementSingleSelected = useSelector(selectIsElementSingleSelected);
    const selectLabelsForElement = useMemo(() => createSelectLabelsForElement(elementId), [elementId]);
    const labelsById = useSelector(selectLabelsForElement);

    // The taskId keys global single-chip selection across the whole task list.
    const containerId = attrs.taskId ?? '';

    const descriptors = useMemo(() => getTaskItemChipDescriptors(attrs, labelsById), [attrs, labelsById]);

    if (!descriptors.length) return null;

    return (
        <ChipSelectionScopeProvider
            containerId={containerId}
            elementId={elementId}
            isElementSingleSelected={isElementSingleSelected}
        >
            <div className="TaskItemChipsContainer flex flex-row flex-wrap gap-sm">
                {descriptors.map((chip) => (
                    <TaskItemChip
                        key={chip.id}
                        chip={chip}
                        size={size}
                        currentUserId={currentUserId ?? undefined}
                        canModify={canModify}
                        attrs={attrs}
                        updateAttributes={updateAttributes}
                    />
                ))}
            </div>
        </ChipSelectionScopeProvider>
    );
};

export default TaskItemChipsContainer;
